import { Component, Element, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { CheckboxSchemeConfiguration } from "../../constants/colors";
import { CheckboxGroupLabelFormat } from "../../constants/global-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { CHECKBOX_SCHEME_CONFIG, CHECKBOX_SCHEME_MAP } from "../checkbox/constants/checkbox-constants";
import { CheckboxSchemeDefinition, CheckboxSchemeName } from "../checkbox/constants/checkbox-types";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - Holds any number of eon-ui-checkbox elements
 */
@Component({
  tag: "eon-ui-checkbox-group",
  styleUrl: "checkbox-group.scss",
  shadow: true
})
export class CheckboxGroup implements ColorableComponent<CheckboxSchemeConfiguration<CheckboxSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: CheckboxSchemeConfiguration<CheckboxSchemeDefinition> = CHECKBOX_SCHEME_CONFIG;
  schemeMap = CHECKBOX_SCHEME_MAP;

  private checkboxes: HTMLEonUiCheckboxElement[];

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiCheckboxGroupElement;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop() scheme: CheckboxSchemeName;

  /**
   * A property to set the legend title of the CheckboxGroup.
   * If not set, the legend will not appear.
   */
  @Prop({ mutable: true }) label: string = "";

  /**
   * Define the size of the input element
   */
  @Prop({ mutable: true }) size: "small" | "normal" = "normal";

  /**
   * A property to format the group label
   */
  @Prop() groupLabelFormat: CheckboxGroupLabelFormat;

  /**
   * A property to move checkboxes to the horizontal position
   */
  @Prop() horizontalAlignment: boolean = false;

  @State() isInfoIconAvailable: boolean;

  @State() activeScheme: CheckboxSchemeDefinition;

  componentWillLoad() {
    this.checkboxes = Array.from(this.hostElement.children).filter.call(
      this.hostElement.children,
      function (child: HTMLElement) {
        return child.tagName === "EON-UI-CHECKBOX";
      }
    );
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="group-info-icon"]');
    this.applyScheme();
    this.adjustSize();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
    this.checkboxes.forEach((checkbox) => {
      checkbox.scheme = this.scheme;
    });
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("size")
  adjustSize() {
    this.checkboxes.forEach((checkbox: HTMLEonUiCheckboxElement) => {
      this.size && (checkbox.size = this.size);
    });
  }

  render() {
    return (
      <Host>
        {this.label && (
          <legend
            class={pfx({
              ["checkbox-group-title"]: true,
              [this.groupLabelFormat]: !!this.groupLabelFormat
            })}
          >
            <span class={pfx({ ["content-wrapper"]: true, ["title-icon"]: this.isInfoIconAvailable })}>
              {this.label}
              {this.isInfoIconAvailable && (
                <span class="group-info-icon">
                  <slot name="group-info-icon" />
                </span>
              )}
            </span>
          </legend>
        )}
        <div
          class={pfx({
            ["checkbox-group-wrapper"]: true,
            ["horizontal-align"]: this.horizontalAlignment
          })}
        >
          <slot />
        </div>
      </Host>
    );
  }
}
